import React, { useEffect, useState } from 'react';
import audioPlayGrantService, {
  PlayGrantError,
} from '../../services/audioPlayGrantService';

export interface AudioPlayerProps {
  /** Root-relative or absolute URL the client POSTs to mint a short-lived playUrl. */
  playGrantUrl: string;
  className?: string;
}

type PlayerStatus = 'loading' | 'ready' | 'error';

function formatPlayGrantError(error: unknown): string {
  const playError =
    error && typeof error === 'object' && 'code' in error && 'message' in error
      ? (error as PlayGrantError)
      : null;

  if (!playError || typeof playError.message !== 'string') {
    return 'Could not get a play link.';
  }

  const parts: string[] = [];
  if (playError.code) {
    parts.push(playError.code);
  }
  if (typeof playError.status === 'number') {
    parts.push(`HTTP ${playError.status}`);
  }
  const prefix = parts.length > 0 ? `${parts.join(' · ')}: ` : '';
  return `${prefix}${playError.message}`;
}

/**
 * Generic play-grant audio player for `<Audio-Player playGrantUrl=… />`.
 * Fetches a short-lived playUrl via authenticated POST, then renders native controls.
 * Does not autoplay — Mobile Safari requires a user gesture on the audio element.
 */
const AudioPlayer: React.FC<AudioPlayerProps> = ({ playGrantUrl, className = '' }) => {
  const [status, setStatus] = useState<PlayerStatus>('loading');
  const [playUrl, setPlayUrl] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function loadGrant() {
      setStatus('loading');
      setErrorMessage(null);
      setPlayUrl(null);

      try {
        const grant = await audioPlayGrantService.requestPlayGrant(playGrantUrl);
        if (cancelled) {
          return;
        }
        setPlayUrl(grant.playUrl);
        setStatus('ready');
      } catch (error) {
        if (cancelled) {
          return;
        }
        console.error('[AudioPlayer] play grant failed', {
          playGrantUrl,
          error,
        });
        setErrorMessage(formatPlayGrantError(error));
        setStatus('error');
      }
    }

    void loadGrant();

    return () => {
      cancelled = true;
    };
  }, [playGrantUrl]);

  return (
    <div
      className={`audio-player my-3 space-y-2 ${className}`.trim()}
      data-testid="audio-player"
    >
      {status === 'loading' && (
        <p className="text-sm opacity-80" role="status" data-testid="audio-player-loading">
          Getting play link…
        </p>
      )}

      {status === 'error' && (
        <p className="text-sm opacity-90" role="alert" data-testid="audio-player-error">
          {errorMessage || 'Could not get a play link.'}
        </p>
      )}

      {status === 'ready' && playUrl && (
        <audio
          key={playUrl}
          controls
          playsInline
          preload="metadata"
          src={playUrl}
          className="w-full max-w-xl"
          data-testid="audio-player-element"
        >
          Your browser does not support audio playback.
        </audio>
      )}
    </div>
  );
};

export default AudioPlayer;
